import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { SwitchTransition, Transition } from "react-transition-group";
import Alert from "@shared/v2/Alert";
import Button from "@shared/v2/Button";
import Modal from "@shared/v2/Modal";
import Tab from "@shared/v2/Tab";
import TabBar from "@shared/v2/TabBar";
import useProductionGoals from "../../services/useProductionGoals";
import Loading from "../Loader";
import { useCurrentUser, useFeatureFlags } from "../../../reducers/layoutReducer/selectors";
import TransactionDataSettings from "./TransactionDataSettings";
import ProductivitySettings from "./ProductivitySettings";
import ClosingConversionSettings from "./ClosingConversionSettings";
import getTransitionStyles from "../../../AddOnsBilling/utils/getTransitionStyles";

interface GoalSettingsModalProps {
  show: boolean;
  onClose: () => void;
  initialTab?: GoalSettingsTab;
  onSaved?: () => void;
  userUuid?: string | null;
  targetUserName?: string | null;
}

type GoalSettingsTab = "Transaction Data" | "Productivity" | "Closing Conversions";

const TABS: Record<
  GoalSettingsTab,
  React.ComponentType<{
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    productionGoals: ReturnType<typeof useProductionGoals>;
    isAdmin?: boolean;
    targetUserName?: string | null;
  }>
> = {
  "Transaction Data": TransactionDataSettings,
  Productivity: ProductivitySettings,
  "Closing Conversions": ClosingConversionSettings,
};

const TAB_ORDER: GoalSettingsTab[] = ["Transaction Data", "Productivity", "Closing Conversions"];

const GoalSettingsModal = ({
  show,
  onClose,
  initialTab,
  onSaved,
  userUuid,
  targetUserName,
}: GoalSettingsModalProps) => {
  const featureFlags = useFeatureFlags() as { goalWizard2: boolean };
  const currentUser = useCurrentUser() as { account: { isAdmin: boolean } };
  const productionGoals = useProductionGoals({ show, userUuid });
  const defaultTab = "Transaction Data";
  const [tab, setTab] = useState<GoalSettingsTab>(initialTab || defaultTab);
  const directionRef = useRef<"left" | "right">("right");
  const tabRefs = {
    "Transaction Data": useRef<HTMLDivElement | null>(null),
    Productivity: useRef<HTMLDivElement | null>(null),
    "Closing Conversions": useRef<HTMLDivElement | null>(null),
  };

  const handleTabChange = (newTab: GoalSettingsTab) => {
    if (newTab === tab) return;
    const currentIndex = TAB_ORDER.indexOf(tab);
    const newIndex = TAB_ORDER.indexOf(newTab);
    directionRef.current = newIndex > currentIndex ? "right" : "left";
    setTab(newTab);
  };

  useEffect(() => {
    if (!show) return;
    setTab(initialTab || defaultTab);
  }, [defaultTab, initialTab, show]);

  const ActiveTab = TABS[tab];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let formattedValue = value;
    const cleanValue = value.replace(/[^0-9.]/g, "");
    const endsWithDecimal = cleanValue.endsWith(".");
    formattedValue =
      cleanValue === "" ? "" : `${parseFloat(cleanValue).toLocaleString()}${endsWithDecimal ? "." : ""}`;
    productionGoals.actions.setGoals((prev) => ({ ...prev, [name]: formattedValue }));
  };

  return (
    <Modal
      className="tw-flex tw-items-center tw-justify-center"
      contentClassName="tw-max-w-[720px] tw-w-[100vw] tw-max-h-[85vh] tw-flex tw-flex-col tw-gap-[32px]"
      show={show}
      onHide={onClose}
    >
      {/* @ts-expect-error props */}
      <Modal.Header closeButton title="Goal Settings" />
      <Modal.Body className="tw-relative tw-overflow-x-hidden tw-overflow-y-auto">
        {productionGoals.loading && <Loading />}
        {productionGoals.errors.global && (
          <div className="tw-mb-[24px]">
            <Alert variant="error" text={productionGoals.errors.global} />
          </div>
        )}
        {featureFlags.goalWizard2 && currentUser.account.isAdmin && (
          <TabBar containerClassName="tw-mb-[24px]">
            {/* @ts-expect-error props */}
            <Tab
              label="Transaction Data"
              onClick={() => handleTabChange("Transaction Data")}
              selected={tab === "Transaction Data"}
            />
            {/* @ts-expect-error props */}
            <Tab
              label="Productivity"
              onClick={() => handleTabChange("Productivity")}
              selected={tab === "Productivity"}
            />
            {/* @ts-expect-error props */}
            <Tab
              label="Closing Conversions"
              onClick={() => handleTabChange("Closing Conversions")}
              selected={tab === "Closing Conversions"}
            />
          </TabBar>
        )}

        <SwitchTransition mode="out-in">
          <Transition
            key={tab}
            nodeRef={tabRefs[tab]}
            timeout={200}
            onEnter={() => tabRefs[tab].current?.offsetHeight}
          >
            {(state) => (
              <div ref={tabRefs[tab]} style={getTransitionStyles(state, directionRef.current)}>
                <ActiveTab
                  onChange={handleChange}
                  productionGoals={productionGoals}
                  isAdmin={currentUser.account.isAdmin}
                  targetUserName={targetUserName}
                />
              </div>
            )}
          </Transition>
        </SwitchTransition>
      </Modal.Body>
      <Modal.Footer className="tw-flex tw-justify-between tw-gap-[8px]">
        {/* @ts-expect-error props */}
        <Button size="medium" schema="tertiary" onClick={onClose} disabled={productionGoals.loading}>
          Cancel
        </Button>
        {/* @ts-expect-error props */}
        <Button
          size="medium"
          isLoading={productionGoals.loading}
          onClick={() =>
            productionGoals.actions.saveGoals().then((success) => {
              if (success) {
                onSaved?.();
                onClose();
              }
            })
          }
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

GoalSettingsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialTab: PropTypes.oneOf(["Transaction Data", "Productivity", "Closing Conversions"]),
  onSaved: PropTypes.func,
  userUuid: PropTypes.string,
  targetUserName: PropTypes.string,
};

GoalSettingsModal.defaultProps = {
  initialTab: null,
  onSaved: null,
  userUuid: null,
  targetUserName: null,
};

export default GoalSettingsModal;
